import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-3ae34961"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "cm-align-center gap-x-10" }

import { ref, reactive, computed, PropType, nextTick, watch, onMounted } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import { TableActOpt, TableOpt } from '@/components/cmTable/types'
import {
  TableViewMode,
  TableViewModeType,
  TABLE_PREVIEW_PAGE_SIZE,
  TABLE_FULL_PAGE_SIZE_DEFAULT
} from '@/views/dashboard/_enum/table-view-mode'
import {
  BaseLineService,
  BaseLineItem,
  BaselineTerminalDrawerInitialPatch,
  FetchOptions
} from '@/views/dashboard/safe-status/base-line/base-line.service'
import {
  BaseLineRepairDrawerService,
  RepairParams,
} from '@/views/dashboard/safe-status/base-line/_components/baseLine-repair-drawer/baseLine-repair-drawer.service'
import type { BaselineTerminalTopItem } from '@/views/dashboard/safe-status/base-line/_components/base-line-header/base-line-header.service'
import { TerminalType, TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import UnauthorizedTooltip from '@/views/dashboard/_components/unauthorized-tooltip/unauthorized-tooltip.vue'
import { listCol } from '@/utils/list-columns-i18n'
import { formatTimeStamp } from '@/utils/time'
import { resolveTerminalNonComplianceCount } from '@/views/dashboard/safe-status/base-line/baseline-repair-items-cache'

const TERMINAL_CHART_TOP_N = 5


export default /*#__PURE__*/_defineComponent({
  __name: 'baseline-terminal-table',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  initialQueryPatch: {
    type: Object as PropType<BaselineTerminalDrawerInitialPatch | undefined>,
    default: undefined
  }
},
  emits: ["viewAll", "detail", "repair", "batchRepair", "syncRepairTaskId", "refresh", "previewChartRows"],
  setup(__props, { expose: __expose, emit: __emit }) {

const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth
}

function isRowRepairDisabled(row: BaseLineItem) {
  return getAuthByList(row.auth_status, ModulePermissionKey[row.area_type!]) !== TerminalAuthStatus.normal
}

const props = __props

const emit = __emit

const injector = useReflectiveInjector([BaseLineService, BaseLineRepairDrawerService])
const baseLineService = injector.get(BaseLineService)
const repairDrawerService = injector.get(BaseLineRepairDrawerService)
const tableRef = ref<any>(null)
const { refreshList } = useCmTableListRefresh(tableRef)
const { t, locale } = useI18n()

const pageOption = { pageNumName: 'page', pageSizeName: 'limit' }

const LINUX_PERMISSION_NAMES = [PermissionFe.linux_base_auth]

/** 与 BaselineTerminalTopRanking max-items 一致，取当前终端列表前 N 条 */
function mapTerminalListToChartRows(list: BaseLineItem[]): BaselineTerminalTopItem[] {
  return (list || []).slice(0, TERMINAL_CHART_TOP_N).map(r => ({
    computer_name: r.computer_name,
    group_name: r.group_name,
    substandard_count: resolveTerminalNonComplianceCount(r),
    device: r
  }))
}

const form = reactive({
  search_word: '',
  group_id: -1,
  system_type: undefined as number | undefined,
  check_result: undefined as number | undefined,
  substandard_id: undefined as string | undefined,
  stale_scan_gt_days: undefined as number | undefined,
  unscanned_over_days: undefined as number | undefined,
  sort: 'check_time',
  asc: false,
  permission_names: [...LINUX_PERMISSION_NAMES]
})

watch(
  () => props.initialQueryPatch,
  (patch, oldPatch) => {
    if (props.viewMode !== TableViewMode.full || patch == null) return
    if (patch.check_result !== undefined) form.check_result = patch.check_result
    if (patch.system_type !== undefined) form.system_type = patch.system_type
    if (patch.group_id !== undefined) form.group_id = patch.group_id
    if (Object.prototype.hasOwnProperty.call(patch, 'substandard_id')) {
      const sid = patch.substandard_id
      form.substandard_id =
        typeof sid === 'string' && sid.trim() ? sid.trim() : undefined
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'stale_scan_gt_days')) {
      form.stale_scan_gt_days = patch.stale_scan_gt_days
    } else {
      form.stale_scan_gt_days = undefined
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'unscanned_over_days')) {
      form.unscanned_over_days = patch.unscanned_over_days
    } else {
      form.unscanned_over_days = undefined
    }
    // 首次仅合并表单，由 onMounted 统一 getList，避免与 CmTable 首载或分组树默认选中重复请求
    if (oldPatch === undefined) return
    nextTick(() => void refreshList())
  },
  { deep: true, immediate: true }
)

onMounted(() => {
  nextTick(() => void refreshList())
})

const resultOptions = computed(() => [
  { label: t('safeProtectionModule.compliant'), value: 1 },
  { label: t('safeProtectionModule.nonCompliant'), value: 2 }
])

const repairCheckItemTarget = computed(() => {
  const id = props.initialQueryPatch?.repairCheckItemId
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : ''
})

/** 检测项列表「详情/修复」的终端抽屉不展示行内操作列 */
const hideTerminalRowActions = computed(
  () => props.initialQueryPatch?.hideTerminalRowActions === true,
)

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  void repairCheckItemTarget.value
  return {
    columnOpt: {
      fixed: 'right',
      width: 180,'show-overflow-tooltip': false,
      label: listCol(t, 'operation'),
    },
    actionButtons: (row?: BaseLineItem) => {
      if (repairCheckItemTarget.value) return []
      return [
        {
          type: 'repair',
          name: t('baselineDashboard.scan'),
          disabled: () => isRowRepairDisabled(row),
          opt: { type: 'text', size: 'small' },
        },
      ]
    },
  }
})

const formColumns = computed(() => {
  void locale.value
  void props.initialQueryPatch?.hideComplianceFilter
  void props.initialQueryPatch?.repairCheckItemId
  const cols: ObjectFormItem<typeof form, 'group_id' | 'system_type' | 'check_result'> = {
    group_id: {
      colOpt: { style:'flex:0 0 150px' },
      slot: true,
      label: t('safeProtectionModule.group'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: { change: handleQuery }
    },
    system_type: {
      prop: 'system_type',
      label: t('safeProtectionModule.os'),
      type: FormType.Select,
      colOpt: { style: 'width: 160px; flex: 0 0 auto' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      dictData: [
        ...TerminalTypeOptions.map(o => ({ label: o.label, value: +o.value }))
      ],
      selectOpt: { clearable: true },
      events: { change: handleQuery }
    },
    check_result: {
      prop: 'check_result',
      label: t('safeProtectionModule.scanResultLabel'),
      type: FormType.Select,
      colOpt: { style: 'width: 160px; flex: 0 0 auto' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      dictData: [...resultOptions.value],
      events: { change: handleQuery },
      selectOpt: { clearable: true },
      hidden: () => props.initialQueryPatch?.hideComplianceFilter === true,
    }
  }
  return cols
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    selection: Boolean(repairCheckItemTarget.value),

    columns: [
      {
        prop: 'computer_name',
        label: listCol(t, 'computerName'),
        columnOpt: { minWidth: 200 },
        showOverflow: true,
        type: 'COMPUTER'
      },
      {
        prop: 'group_name',
        label: listCol(t, 'groupName'),
        slotName: 'group_name',
        columnOpt: { minWidth: 100 }
      },
      { prop: 'user_mark', label: listCol(t, 'userMark'), columnOpt: { minWidth: 100 } },
      { prop: 'client_ip', label: listCol(t, 'ipAddress'), type: 'IP', columnOpt: { minWidth: 140 } },
      { prop: 'system_name', label: t('safeProtectionModule.os'), columnOpt: { minWidth: 120 }, showOverflow: true },
      {
        prop: 'scan_result',
        label: t('safeProtectionModule.scanResultLabel'),
        slotName: 'scan_result',
        columnOpt: { minWidth: 160 },
        sortable: 'custom'
      },
      {
        prop: 'scan_status',
        label: t('baselineDashboard.scanStatus'),
        slotName: 'scan_status',
        columnOpt: { minWidth: 100 }
      },
      {
        prop: 'check_time',
        label: t('safeProtectionModule.lastScanTimeShort'),
        columnOpt: { minWidth: 160 },
        sortable: 'custom',
        formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm'),
      },
    ],
    usePagination: false,
    showSearch: false,
    showForm: false,
    showActionBar: false,
    showRightToolbar: true,
    rowKey: 'device_id',
    path: 'list',
    totalPath: 'total',
    computerClick: (row: BaseLineItem) => emit('detail', row),
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    hiddenPageTool: props.viewMode === TableViewMode.preview,
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined
  }
})

function scanStatusText(row: BaseLineItem) {
  return row.check_time ? t('baselineDashboard.scanStatusDone') : t('baselineDashboard.scanStatusPending')
}

function getListApi(params: Record<string, unknown>) {
  const isPreview = props.viewMode === TableViewMode.preview
  const page = isPreview ? 1 : (Number(params.page) || 1)
  const limit = isPreview ? TABLE_PREVIEW_PAGE_SIZE : (Number(params.limit) || TABLE_FULL_PAGE_SIZE_DEFAULT)
  const gid = params.group_id
  const sid = params.substandard_id
  const staleDays = params.stale_scan_gt_days as number | undefined
  const unscannedDays = params.unscanned_over_days as number | undefined
  const req = baseLineService.getBaseLineData({
    page,
    limit,
    search_word: String(params.search_word ?? ''),
    group_id: typeof gid === 'number' ? gid : -1,
    system_type: params.system_type as number | undefined,
    check_result: params.check_result as number | undefined,
    substandard_id: typeof sid === 'string' && sid.trim() ? sid.trim() : undefined,
    ...(staleDays !== undefined && staleDays !== null ? { stale_scan_gt_days: staleDays } : {}),
    ...(unscannedDays !== undefined && unscannedDays !== null
      ? { unscanned_over_days: unscannedDays }
      : {}),
    asc: (params.asc as boolean) ?? false,
    sort: (params.sort as string) || 'check_time',
    permission_names: (params.permission_names as string[]) ?? LINUX_PERMISSION_NAMES
  } as FetchOptions)
  if (!isPreview) return req
  return req
    .then(res => {
      emit('previewChartRows', mapTerminalListToChartRows(res.list || []))
      return res
    })
    .catch(err => {
      emit('previewChartRows', [])
      return Promise.reject(err)
    })
}

async function handleSearch(value: string) {
  form.search_word = value
  await refreshList()
}

async function handleQuery() {
  await refreshList()
}

function updateGroupId(groupId: number) {
  if (form.group_id === groupId) return
  form.group_id = groupId
  void handleQuery()
}

function handleReset() {
  form.search_word = ''
  form.group_id = -1
  form.system_type = undefined
  const lock = props.initialQueryPatch?.hideComplianceFilter === true
  if (!lock) {
    form.check_result = undefined
    form.substandard_id = undefined
  } else {
    if (props.initialQueryPatch?.check_result !== undefined) {
      form.check_result = undefined
    }
    if (Object.prototype.hasOwnProperty.call(props.initialQueryPatch ?? {}, 'substandard_id')) {
      const sid = props.initialQueryPatch?.substandard_id
      form.substandard_id =
        typeof sid === 'string' && sid.trim() ? sid.trim() : undefined
    }
  }
  form.sort = 'check_time'
  form.asc = false
  form.permission_names = [...LINUX_PERMISSION_NAMES]
  form.stale_scan_gt_days = undefined
  if (props.initialQueryPatch?.stale_scan_gt_days !== undefined) {
    form.stale_scan_gt_days = props.initialQueryPatch.stale_scan_gt_days
  }
  form.unscanned_over_days = undefined
  if (props.initialQueryPatch?.unscanned_over_days !== undefined) {
    form.unscanned_over_days = props.initialQueryPatch.unscanned_over_days
  }
  void refreshList()
}

const multipleSelection = ref<BaseLineItem[]>([])
function handleSelectionChange(selection: BaseLineItem[]) {
  multipleSelection.value = selection
}

async function onRepairSelectedForCheckItem() {
  const checkId = repairCheckItemTarget.value
  if (!checkId) return
  const devices = multipleSelection.value
  if (!devices.length) {
    ElMessage.warning(t('baselineDashboard.repairSelectTerminalsFirst'))
    return
  }
  repairDrawerService.initBaseData()
  try {
    const subRes = await repairDrawerService.getSubStandardIds({
      select_type: 1,
      group_id: form.group_id,
      device_list: devices.map((d) => d.device_id),
    })
    const subs = subRes.substandard_ids ?? []
    if (!subs.includes(checkId)) {
      ElMessage.warning(t('baselineDashboard.repairCheckItemNotOnSelectedTerminals'))
      return
    }
    const lens = repairDrawerService.generateIDs([checkId])
    if (!lens.alen && !lens.nlen) {
      ElMessage.warning(t('safeProtectionModule.noRepairSelected'))
      return
    }
    const params: RepairParams = {
      select_type: 1,
      device_list: devices.map((d) => d.device_id),
      group_id: form.group_id,
      repair_item_ids: lens.autoIDs,
      notice_item_ids: lens.notifyIDs,
    }
    const res = await repairDrawerService.getRepairNotification(params)
    if (lens.alen) {
      emit('syncRepairTaskId', res.task_id)
    } else {
      ElMessage.success(t('safeProtectionModule.repairNotifySent'))
    }
    emit('refresh')
  } catch {
    ElMessage.error(t('baselineDashboard.operateFailed'))
  }
}

__expose({
  getList: () => void refreshList(),
  getSelection: () => multipleSelection.value
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    modelValue: form,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form) = $event)),
    "table-opt": tableOpt.value,
    "table-act-opt": tableActOpt.value,
    api: getListApi,
    "page-option": pageOption,
    "first-loading": false,
    onChangeSelected: handleSelectionChange
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: form,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form) = $event)),
        "form-columns": formColumns.value,
        "show-search": true,
        "search-placeholder": _ctx.$t('common.phComputerUserIp'),
        "popover-threshold": 0,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleReset
      }, {
        group_id: _withCtx(() => [
          _createVNode(GroupTreeSelector, {
            width: "100%",
            "terminal-type": [_unref(TerminalType).linux],
            "default-group-id": form.group_id,
            onSelected: updateGroupId
          }, null, 8, ["terminal-type", "default-group-id"])
        ]),
        actions: _withCtx(() => [
          (repairCheckItemTarget.value && __props.viewMode === _unref(TableViewMode).full)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                size: "small",
                disabled: !multipleSelection.value.length,
                onClick: onRepairSelectedForCheckItem
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('baselineDashboard.repair')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true),
          (__props.viewMode === _unref(TableViewMode).preview)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 1,
                type: "primary",
                size: "small",
                plain: "",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('viewAll')))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "search-placeholder"])
    ]),
    scan_result: _withCtx(({ row }: any) => [
      (_unref(resolveTerminalNonComplianceCount)(row as BaseLineItem))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createTextVNode(_toDisplayString(_ctx.$t('safeProtectionModule.nonCompliant')) + " ", 1),
            _createVNode(_component_el_button, {
              type: "text",
              size: "small",
              class: "red-text",
              onClick: ($event: any) => (emit('detail', row as BaseLineItem))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(resolveTerminalNonComplianceCount)(row as BaseLineItem)), 1)
              ]),
              _: 2
            }, 1032, ["onClick"]),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('safeProtectionModule.itemsCountSuffix')), 1)
          ], 64))
        : (_openBlock(), _createBlock(_component_el_button, {
            key: 1,
            type: "text",
            size: "small",
            class: "green-text",
            onClick: ($event: any) => (emit('detail', row as BaseLineItem))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('safeProtectionModule.compliant')), 1)
            ]),
            _: 2
          }, 1032, ["onClick"]))
    ]),
    scan_status: _withCtx(({ row }: any) => [
      _createTextVNode(_toDisplayString(scanStatusText(row as BaseLineItem)), 1)
    ]),
    "repair-button-action": _withCtx(({ row }: any) => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(UnauthorizedTooltip, {
          "permission-key": ModulePermissionKey[row.area_type!],
          "device-id": row.device_id,
          "auth-status": _unref(getAuthByList)(row.auth_status, ModulePermissionKey[row.area_type!]),
          "area-type": row.area_type,
          onAuthSuccess: _cache[2] || (_cache[2] = ($event: any) => (emit('refresh')))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "text",
              size: "small",
              disabled: isRowRepairDisabled(row),
              onClick: ($event: any) => (emit('repair', row))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('baselineDashboard.scan')), 1)
              ]),
              _: 2
            }, 1032, ["disabled", "onClick"])
          ]),
          _: 2
        }, 1032, ["permission-key", "device-id", "auth-status", "area-type"]),
        _createVNode(_component_el_button, {
          type: "text",
          size: "small",
          onClick: ($event: any) => (emit('detail', row))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(listCol)(_unref(t), 'detail')), 1)
          ]),
          _: 2
        }, 1032, ["onClick"])
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "table-opt", "table-act-opt"]))
}
}

})